import React, {useEffect, useMemo, useRef, useState} from 'react';
import {flushSync} from 'react-dom';
import moment from 'moment';
import usePageTitle from '../../hooks/usePageTitle';
import {useDispatch, useSelector} from 'react-redux';

import MonthlyLeaderboard from '../../components/MonthlyLeaderboard';
import MonthlyDailyRanks from '../../components/MonthlyDailyRanks';
import MonthlySectionHeader from '../../components/MonthlySectionHeader';
import TrackSegmentedControl from '../../components/TrackSegmentedControl';
import MonthScopeSheet from '../../components/MonthScopeSheet';
import PerHorizonComparison from '../../components/PerHorizonComparison';
import ForecastComparisonChartUplot from '../../components/ForecastComparisonChartUplot';
import ChartDashboardActions from '../../components/ChartDashboardActions';
import ChartChips from '../../components/ChartChips';
import ChartOptionsSheet, { periodChipLabel } from '../../components/ChartOptionsSheet';
import StatsCard from '../../components/StatsCards';
import Card, { CardHeader } from '../../components/ui/Card';
import SubmissionsActivityChart from '../../components/SubmissionsActivityChart';
import ConfirmDialog from '../../components/ConfirmDialog';
import StatusHero from '../../components/StatusHero';
import DashboardNotices from '../../components/notices/DashboardNotices';
import AlsoDueRow from '../../components/AlsoDueRow';
import MobileRankStrip from '../../components/MobileRankStrip';
import MonthlyTabs from '../../components/MonthlyTabs';
import MonthlyPerformanceCard from '../../components/MonthlyPerformanceCard';
import MonthlySubmissions from '../../components/MonthlySubmissions';
import PerformancePane from '../../components/performancePane/PerformancePane';
import PerformancePaneHandle from '../../components/performancePane/PerformancePaneHandle';
import { ChevronDownIcon, ArrowsPointingOutIcon } from '@heroicons/react/24/outline';
import FullScreenChartOverlay from '../../components/FullScreenChartOverlay';
import {useBreadcrumbs} from '../../contexts/BreadcrumbContext';
import {resourceSource} from '../../helpers/marketProfile';
import { resourceUnitMeta } from '../../utils/resourceUnits';

import {
  fetchUsers,
} from '../../slices/userSlice';

import {
  fetchMarketSession,
  fetchSubmissionStats,
  fetchSubmissionActivity,
  fetchMarketForecasts,
  fetchAvailableHorizons,
  fetchComparisonForecasts,
  fetchResourceParticipantsRange,
  selectEnsembleForecasts,
  selectMarketSessions,
  selectTotalSubmissions,
  selectActiveForecasters,
  selectMarketActiveUsers,
  selectSubmissionActivity,
  selectMarketStartDate,
  selectMarketEndDate,
  selectDownloadReportLoading,
  selectModel,
  selectIncludeEnsemble,
  selectAnonymize,
  selectHorizonFilter,
  selectAvailableHorizons,
  selectComparisonModels,
  selectComparisonForecasts,
  selectResourceParticipantsRange,
  setClearEnsembleForecasts,
  clearComparisonForecasts,
  clearResourceParticipantsRange,
  setLastFetchSignature,
  setDateRange,
  setHorizonFilter,
} from '../../slices/marketSlice';
import {toast} from "react-toastify";
import {
  fetchRawData,
  fetchUserForecastData,
  fetchUserContinuousForecasts,
  fetchMultipleUserForecasts,
  selectRawData,
  selectUserForecastData,
  selectContinuousForecastData,
  selectIndividualUserForecasts,
  selectIndividualAppendsInFlight,
  selectLoading,
  clearRawData,
  clearUserForecastData,
  clearContinuousForecastData,
  clearIndividualUserForecasts,
  removeIndividualUserForecasts,
  setSelectedForecast,
} from '../../slices/dataSlice';

import {
  fetchActiveUsers,
  fetchHorizonOverview,
  selectActiveUsers,
  selectHorizonOverviewData,
} from '../../slices/reportsSlice';
import {selectAuthIsAdmin, selectAuthIsMarketOwner, selectAuthIsMarketOwnerReadOnly, selectAuthIsForecaster, selectAuthUser} from '../../slices/authSlice';
import {openPane} from '../../slices/performancePaneSlice';
import PermissionChecker from '../../utils/permissionChecker';
import {
  selectActiveUsersDays,
  selectSelectedResourceId,
  selectBlurSensitive,
  selectInsightsEnabled,
  selectInsightsRampMethod,
  selectInsightsShowPast,
  selectInsightsStrongOnly,
  selectInsightsTypes,
} from '../../slices/preferencesSlice';
import {
  insightsKey,
  openAbout,
  selectCanUseInsights,
  selectInsightsAboutOpen,
  selectInsightsAboutTab,
  selectInsightsAboutTabSeq,
  selectInsightsData,
  selectInsightsPastCutoff,
  setAboutOpen,
} from '../../slices/insightsSlice';
import AboutInsightsPanel from '../../components/insights/AboutInsightsPanel';
import useInsightsFetch from '../../components/insights/useInsightsFetch';
import { rowsWithoutForecastRamps } from '../../components/insights/prepare';
import useMediaQuery from '../../hooks/useMediaQuery';
import useSelectedResource from '../../hooks/useSelectedResource';
import { DEFAULT_SELECTED_FORECAST } from './dashboardFilterRevert';
import { buildContinuousPreview } from '../../utils/continuousPreview';
import { ENSEMBLE_ID, buildFetchSignature, planForecasterFetch, signatureBase } from '../../utils/forecasterFetchPlan';

// Phone chart chip label for each forecaster series mode.
const PREVIEW_SHORT = { both: 'All series', submissions: 'Submissions', pending: 'Continuous' };

// Stable stand-in for a hidden series, so hiding one does not hand the chart a
// new array (and rebuild it) on every render.
const NO_SERIES = [];

export default function Dashboard() {
  usePageTitle('Dashboard');
  const dispatch = useDispatch();

  // --- Redux Selectors ---
  const {usersList} = useSelector((state) => state.user);
  const participants = useSelector(selectResourceParticipantsRange);
  const ensembleForecasts = useSelector(selectEnsembleForecasts);
  const rawData = useSelector(selectRawData);
  const userForecastData = useSelector(selectUserForecastData);
  const continuousForecastData = useSelector(selectContinuousForecastData);
  const startDate = useSelector(selectMarketStartDate);
  const endDate = useSelector(selectMarketEndDate);
  const activeUsersDays = useSelector(selectActiveUsersDays);
  const marketSessions = useSelector(selectMarketSessions);
  const totalSubmissions = useSelector(selectTotalSubmissions);
  const activeForecasters = useSelector(selectActiveForecasters);
  const activeUsers = useSelector(selectMarketActiveUsers);
  const submissionActivity = useSelector(selectSubmissionActivity);
  const selectedResource = useSelectedResource();
  const reportDownloadLoading = useSelector(selectDownloadReportLoading);
  const model = useSelector(selectModel);
  const includeEnsemble = useSelector(selectIncludeEnsemble);
  const anonymize = useSelector(selectAnonymize);
  const blurSensitive = useSelector(selectBlurSensitive);
  const horizonFilter = useSelector(selectHorizonFilter);
  const availableHorizons = useSelector(selectAvailableHorizons);
  const comparisonModels = useSelector(selectComparisonModels);
  const comparisonForecasts = useSelector(selectComparisonForecasts);
  const selectedResourceId = useSelector(selectSelectedResourceId);
  const isAdmin = useSelector(selectAuthIsAdmin);
  const isMarketOwner = useSelector(selectAuthIsMarketOwner);
  const isMarketOwnerReadOnly = useSelector(selectAuthIsMarketOwnerReadOnly);
  const isForecaster = useSelector(selectAuthIsForecaster);
  const currentUser = useSelector(selectAuthUser);

  // Set breadcrumbs for the header
  const isPrivilegedUser = isAdmin || isMarketOwner || isMarketOwnerReadOnly;
  // Ensemble insights (superusers and market owners): drawn and fetched only once the
  // user turns them on (Insights chip); a stored preference alone does nothing.
  const insightsAvailable = useSelector(selectCanUseInsights);
  // Phones and tablets have no Insights control yet: insights stay off below lg.
  const isDesktop = useMediaQuery('(min-width: 1024px)');
  const insightsOn = useSelector(selectInsightsEnabled) && insightsAvailable && isDesktop;
  const insightsStrongOnly = useSelector(selectInsightsStrongOnly);
  const insightsShowPast = useSelector(selectInsightsShowPast);
  const insightsTypes = useSelector(selectInsightsTypes);
  const insightsRampMethod = useSelector(selectInsightsRampMethod);
  const insightsData = useSelector(selectInsightsData);
  const insightsAboutOpen = useSelector(selectInsightsAboutOpen);
  const insightsAboutTab = useSelector(selectInsightsAboutTab);
  const insightsAboutTabSeq = useSelector(selectInsightsAboutTabSeq);
  const insightsPastCutoff = useSelector(selectInsightsPastCutoff);
  // onAbout: the Observed strip's "What counts as a ramp" link.
  const insightsOptions = useMemo(
    () => ({
      types: insightsTypes, strongOnly: insightsStrongOnly, showPast: insightsShowPast, pastCutoff: insightsPastCutoff,
      onAbout: (tab) => dispatch(openAbout(tab)),
    }),
    [insightsTypes, insightsStrongOnly, insightsShowPast, insightsPastCutoff, dispatch],
  );
  useBreadcrumbs(
    [{ label: 'Dashboard' }],
    isPrivilegedUser ? 'Time-series forecasts and recent scores' : 'Your forecasts, scores and standing',
  );

  // Central permission checker instance
  const permissionChecker = new PermissionChecker({
    isAdmin,
    isMarketOwner,
    isMarketOwnerReadOnly,
    isForecaster,
  });
  const showSide = permissionChecker.satisfies('market_owner_read');
  const loadingData = useSelector(selectLoading);
  const selectedForecast = useSelector((state) => state.data.selectedForecast);
  const individualUserForecasts = useSelector(selectIndividualUserForecasts);

  // --- State for performance warning dialog ---
  const [showPerformanceWarning, setShowPerformanceWarning] = useState(false);
  const [queryEstimate, setQueryEstimate] = useState({});
  // Filters as of the last applied change (a fetch, or a forecaster change
  // applied without a full refetch). Cancelling the performance warning on an
  // addition rolls the selection back to this snapshot: filter edits are
  // committed to Redux optimistically, so without a rollback the picker shows
  // the new selection while the plot keeps the old data.
  const appliedFiltersRef = useRef(null);
  // Forecaster additions in flight. They keep the chart on screen, so their
  // requests do not show the chart's loading spinner.
  const appendsInFlight = useSelector(selectIndividualAppendsInFlight);

  // --- Applied model state: only update when Apply is pressed ---
  const [appliedModel, setAppliedModel] = useState(model);
  const [appliedComparisonModels, setAppliedComparisonModels] = useState(comparisonModels);

  // Forecaster chart display: 'both' (submissions + continuous),
  // 'submissions' (submissions only), or 'pending' (continuous only).
  const [previewMode, setPreviewMode] = useState('both');

  // --- Local loading state to prevent "Chart unavailable" flash during data transitions ---
  const [isTransitioning, setIsTransitioning] = useState(false);
  // Phone-only full-screen chart view (see FullScreenChartOverlay).
  const [chartExpanded, setChartExpanded] = useState(false);
  // Phone chart options sheet (forecasters), opened from the chart chips.
  const [chartSheetOpen, setChartSheetOpen] = useState(false);
  // Phone month-scope sheet ("This month" row: horizon + month together).
  const [scopeOpen, setScopeOpen] = useState(false);

  // --- Shared monthly-filters state (drives MonthlyLeaderboard, MonthlyDailyRanks,
  // and MonthlyPerformanceCard below the plot) ---
  const monthlyNow = moment();
  const [monthlyTrack, setMonthlyTrack] = useState('deterministic');
  const [monthlyYear, setMonthlyYear] = useState(monthlyNow.year());
  const [monthlyMonth, setMonthlyMonth] = useState(monthlyNow.month() + 1);
  const [monthlyHorizon, setMonthlyHorizon] = useState(null);
  const horizonOverviewData = useSelector(selectHorizonOverviewData);
  const monthlyActiveUsers = useSelector(selectActiveUsers);
  const monthlyHorizons = useMemo(() => {
    // Drive the monthly horizon dropdown from the market-wide available
    // horizons (same source as the main plot's horizon picker) rather than a
    // single reference user's MarketMonthlyStats rows. A reference user who
    // skipped a horizon (e.g. intraday) would otherwise hide it for everyone,
    // even though other forecasters have scores for it. Fall back to the
    // overview-derived set if the market list hasn't loaded yet.
    if (Array.isArray(availableHorizons) && availableHorizons.length > 0) {
      return [...availableHorizons].sort((a, b) => a - b);
    }
    if (!Array.isArray(horizonOverviewData)) return [];
    return [...new Set(horizonOverviewData.map((d) => d.horizon_offset))].sort(
      (a, b) => a - b,
    );
  }, [availableHorizons, horizonOverviewData]);

  // --- A local ref to store the last "fetch signature" ---
  const lastFetchSignature = useSelector((state) => state.market.lastFetchSignature);

  const resourceId = selectedResource?.id || selectedResourceId;

  // For non-forecaster roles, fetch the list of active users so we can use
  // a real forecaster id when querying horizon overview / monthly stats
  // (the API falls back to the caller's id, which has no records for
  // market owners or admins).
  useEffect(() => {
    if (!resourceId || isForecaster) return;
    dispatch(fetchActiveUsers({
      resourceId,
      year: monthlyYear,
      month: monthlyMonth,
      track: monthlyTrack,
    }));
  }, [dispatch, resourceId, isForecaster, monthlyYear, monthlyMonth, monthlyTrack]);

  // Resolve the user id used when querying shared monthly endpoints.
  // Prefer a qualified user (has_stats=true) — only their MarketMonthlyStats
  // row carries the shared horizon list / month_scores_json. An unqualified
  // user has only penalty rows, leading to empty horizons → horizon stays
  // null → downstream cards never fetch.
  const monthlyReferenceUserId = useMemo(() => {
    if (isForecaster) return currentUser?.id || null;
    if (Array.isArray(monthlyActiveUsers) && monthlyActiveUsers.length > 0) {
      const qualified = monthlyActiveUsers.find((u) => u.has_stats === true);
      const pick = qualified || monthlyActiveUsers[0];
      return pick.user_id || pick.id || null;
    }
    return null;
  }, [isForecaster, currentUser, monthlyActiveUsers]);

  // Populate the shared horizon dropdown from the monthly-stats overview.
  useEffect(() => {
    if (!resourceId) return;
    if (!isForecaster && !monthlyReferenceUserId) return;
    dispatch(fetchHorizonOverview({
      userId: monthlyReferenceUserId,
      resourceId,
      year: monthlyYear,
      month: monthlyMonth,
      track: monthlyTrack,
    }));
  }, [dispatch, resourceId, isForecaster, monthlyReferenceUserId, monthlyYear, monthlyMonth, monthlyTrack]);

  // Auto-select first horizon when the list populates or current selection drops out.
  useEffect(() => {
    if (monthlyHorizons.length === 0) {
      if (monthlyHorizon !== null) setMonthlyHorizon(null);
      return;
    }
    if (monthlyHorizon == null || !monthlyHorizons.includes(monthlyHorizon)) {
      setMonthlyHorizon(monthlyHorizons[0]);
    }
  }, [monthlyHorizons]); // eslint-disable-line react-hooks/exhaustive-deps

  // Clamp selected month when switching into the current year past its current month.
  useEffect(() => {
    const currentYear = monthlyNow.year();
    const maxMonth = monthlyYear === currentYear ? monthlyNow.month() + 1 : 12;
    if (monthlyMonth > maxMonth) setMonthlyMonth(maxMonth);
  }, [monthlyYear]); // eslint-disable-line react-hooks/exhaustive-deps

  const roleLabel =
    isAdmin
      ? 'admin'
      : isMarketOwner
        ? 'market_owner'
        : isMarketOwnerReadOnly
          ? 'market_owner_read'
          : isForecaster
            ? 'forecaster'
            : 'guest';

  // What every chart series depends on; the forecaster selection is kept apart
  // so a selection change alone can be applied incrementally.
  const baseSignature = [
    resourceId,
    startDate,
    endDate,
    roleLabel,
    model,
    horizonFilter,
    comparisonModels.slice().sort().join(','),
  ].join('|');
  const selectedIds = Array.isArray(selectedForecast)
    ? selectedForecast.map(f => f.id || ENSEMBLE_ID)
    : [];
  const fetchSignature = buildFetchSignature(baseSignature, selectedIds);

  // Pending continuous forecasts (full series). Only built for the
  // forecaster's own view.
  const continuousPreview = useMemo(
    () => (isForecaster ? buildContinuousPreview(continuousForecastData) : []),
    [isForecaster, continuousForecastData],
  );

  // The Submissions/Pending toggle only applies to a forecaster who actually
  // has pending data; otherwise we always show submissions.
  const hasPendingPreview = isForecaster && continuousPreview.length > 0;
  const effectivePreviewMode = hasPendingPreview ? previewMode : 'both';

  // In "Continuous only" mode, hide the forecaster's submission series so the
  // chart shows just the full continuous forecast (observed data still shows).
  const forecasterChartForecast =
    effectivePreviewMode === 'pending' ? [] : userForecastData;

  // In "Submissions only" mode, hide the continuous-forecast preview instead.
  const previewForChart =
    effectivePreviewMode === 'submissions' ? [] : continuousPreview;

  // user id -> username, so the chart tooltip can swap each forecaster's email
  // for their alias when the "hide info" toggle is on.
  const userAliases = useMemo(() => {
    const map = {};
    (usersList || []).forEach((u) => {
      if (u?.id != null) map[u.id] = u.username || '';
    });
    return map;
  }, [usersList]);

  // =========== 1) Mount/Initialization =============
  // Fetch data that only needs to happen once (or if isAdmin changes).
  // This does NOT fetch rawData or forecasts yet, only general info.
  useEffect(() => {
    if (!resourceId) return;

    // Reset participants picker when resource changes; the date-range
    // effect below will refetch once startDate/endDate are settled.
    dispatch(clearResourceParticipantsRange());

    // Reset the view to the default window SYNCHRONOUSLY, in the same render
    // as the resourceId change, so fetchSignature settles to the default dates
    // before the debounced auto-apply effect runs. Deferring this reset (e.g.
    // inside an async .then()) let the debounced effect fire once with the
    // stale wide dates and again after the reset, racing two overlapping
    // fetch cycles.
    // Phones get a tighter default (2 days back through the day-ahead) than
    // desktop (5 days back): a week of quarter-hour peaks is unreadable at
    // 360px. Seeded per resource change only; users widen it via filters.
    const defaultDaysBack = window.matchMedia('(min-width: 1024px)').matches ? 5 : 2;
    dispatch(setDateRange({
      startDate: moment().utc().subtract(defaultDaysBack, 'days').startOf('day').format('YYYY-MM-DD'),
      endDate: moment().utc().add(1, 'days').startOf('day').format('YYYY-MM-DD'),
    }));

    // Available horizons feed the horizon filter UI only; they do not affect
    // the fetch signature, so this can run independently.
    dispatch(fetchAvailableHorizons({ resourceId }));

    // allow market_owner_read and higher to fetch users for stats/recent activity
    if (showSide) {
      dispatch(fetchUsers());
      dispatch(fetchSubmissionStats({activeDays: activeUsersDays}));
      dispatch(fetchSubmissionActivity({
        startDate: moment().subtract(89, 'days').format('YYYY-MM-DD'),
        endDate: moment().add(1, 'days').format('YYYY-MM-DD'),
      }));
    }
    dispatch(fetchMarketSession({status: 'finished'}));

    // The forecast/observed fetch is driven solely by the debounced auto-apply
    // effect below. On mount lastFetchSignature is null and on a resource
    // switch the signature changes (new resourceId + reset dates), so exactly
    // one fetch cycle runs. Issuing a fetch here with the pre-reset dates would
    // race a second cycle against it (see the reset comment above).
  }, [dispatch, resourceId, showSide]); // eslint-disable-line react-hooks/exhaustive-deps

  // Participants depend only on resource + date range, not on the forecast
  // fetch. Keep them on their own effect so they survive the fetchDataIfNeeded
  // signature guard: on SPA navigation back into the dashboard, the store may
  // still hold matching forecasts (so the guard short-circuits) while the mount
  // effect has just cleared participants, leaving the forecaster picker empty
  // until a hard refresh. Fetching here decouples the two.
  useEffect(() => {
    if (!resourceId || !startDate || !endDate) return;
    if (!(isAdmin || isMarketOwner)) return;
    dispatch(fetchResourceParticipantsRange({ startDate, endDate, resourceId }));
  }, [dispatch, resourceId, startDate, endDate, isAdmin, isMarketOwner]);

  // Insights follow the applied filters (a full refetch changes the base of
  // lastFetchSignature), the toggle, the ramp method and the observed-ramps
  // layer (part of the key); useInsightsFetch has the rules.
  useInsightsFetch({
    available: insightsAvailable,
    on: insightsOn,
    filters: {
      resourceId, startDate, endDate, model, horizonFilter, rampMethod: insightsRampMethod, observed: insightsTypes.observed,
    },
    appliedKey: insightsKey(signatureBase(lastFetchSignature), insightsRampMethod, insightsTypes.observed),
  });

  // Check for ANY existing data (raw, ensemble, individual users, or user forecasts)
  const hasExistingData =
    (rawData && rawData.length > 0) ||
    (ensembleForecasts && ensembleForecasts.length > 0) ||
    (individualUserForecasts && Object.keys(individualUserForecasts).length > 0) ||
    (userForecastData && userForecastData.length > 0);

  // =========== 2) Build a function to fetch data IF needed ===========
  const fetchDataIfNeeded = async (signature) => {
    // Build a "signature" string from the parameters that define what data we need
    const resourceId = selectedResource?.id || selectedResourceId;
    if (!resourceId) return;

    // Check if we already have data for this signature
    if (signature === lastFetchSignature && hasExistingData) {
      // Signature matches, so the current filters ARE the applied ones; seed
      // the rollback snapshot (covers SPA nav back with data still in store).
      if (!appliedFiltersRef.current) {
        appliedFiltersRef.current = {
          selectedForecast, startDate, endDate, model, comparisonModels, horizonFilter,
        };
      }
      return;
    }

    // Set transitioning state to prevent "Chart unavailable" flash. Flushed
    // at once: from the debounce timer it is a normal-priority update that
    // would render after the sync-priority store clears below, and while an
    // addition is still in flight that render would paint the empty chart.
    flushSync(() => setIsTransitioning(true));

    // Remember the filters this fetch ran with, so cancelling a later
    // performance warning can roll back to them.
    appliedFiltersRef.current = {
      selectedForecast, startDate, endDate, model, comparisonModels, horizonFilter,
    };

    // Update the ref to the new signature
    dispatch(setLastFetchSignature(signature));
    // Clear existing data so we don't have stale charts
    dispatch(clearRawData());
    dispatch(clearUserForecastData());
    dispatch(clearContinuousForecastData());
    dispatch(clearIndividualUserForecasts());
    dispatch(setClearEnsembleForecasts());
    dispatch(clearComparisonForecasts());
    // Fetch all independent data sources in parallel
    const fetches = [
      dispatch(fetchRawData({
        startDate: `${startDate}T00:00:00Z`,
        endDate: `${endDate}T23:59:59Z`,
        resourceId,
      })),
    ];

    if (isAdmin || isMarketOwner || isMarketOwnerReadOnly) {
      fetches.push(
        dispatch(fetchMarketForecasts({
          startDate, endDate, resourceId, model, horizonFilter,
        })),
      );

      // Participants are fetched by their own effect (decoupled from this
      // signature-guarded path) so the forecaster picker stays populated.

      if (comparisonModels.length > 0) {
        fetches.push(
          dispatch(fetchComparisonForecasts({
            startDate, endDate, resourceId,
            models: comparisonModels, horizonFilter,
          }))
        );
      }

      if (Array.isArray(selectedForecast)) {
        const userIds = selectedForecast
          .filter(f => f.id !== null)
          .map(f => f.id);
        if (userIds.length > 0) {
          fetches.push(
            dispatch(fetchMultipleUserForecasts({
              userIds, startDate, endDate, resourceId, horizonFilter,
            }))
          );
        }
      }
    } else {
      // Forecaster: fetch all 3 quantiles in parallel
      ['q10', 'q50', 'q90'].forEach(variable => {
        fetches.push(
          dispatch(fetchUserForecastData({
            startDate, endDate, resourceId,
            user: currentUser?.id, variable, horizonFilter,
          }))
        );
      });
      // Pending continuous forecasts (preview), same date window. The
      // endpoint self-scopes to the caller, so no user param is sent.
      ['q10', 'q50', 'q90'].forEach(variable => {
        fetches.push(
          dispatch(fetchUserContinuousForecasts({
            startDate, endDate, resourceId, variable,
          }))
        );
      });
    }

    await Promise.allSettled(fetches);

    // Clear transitioning state after all fetches complete
    setIsTransitioning(false);
  };

  // Alternatively, you can remove this entire effect and rely on an explicit "Apply" button.

  // =========== 3) Forecaster changes without a full refetch ===========
  // Admins and market owners: a change to the forecaster selection alone is
  // applied locally (removals, Ensemble) or fetches only the added forecasters.
  // A change of resource, dates, role, model, horizon or comparison models, or
  // an empty chart, refetches everything; forecasters always take that path.
  const planFetch = () => (isPrivilegedUser
    ? planForecasterFetch({
      appliedBase: hasExistingData ? signatureBase(lastFetchSignature) : null,
      nextBase: baseSignature,
      selectedIds,
      // Loaded or still loading; failed ones are left out so a change retries them.
      loadedIds: Object.keys(individualUserForecasts || {})
        .filter((id) => !individualUserForecasts[id]?.error),
    })
    : { kind: 'full', add: [], remove: [] });

  // Records the current filters as applied without a full refetch: drops the
  // removed forecasters and matches lastFetchSignature, so the debounced
  // auto-apply stays idle and hasPendingChanges is false.
  const commitSelection = (removeIds) => {
    if (removeIds.length > 0) dispatch(removeIndividualUserForecasts(removeIds));
    appliedFiltersRef.current = {
      selectedForecast, startDate, endDate, model, comparisonModels, horizonFilter,
    };
    dispatch(setLastFetchSignature(fetchSignature));
  };

  // Fetches only the added forecasters and merges them into the loaded ones:
  // observed data, the ensemble, comparisons and the other lines stay on screen.
  const fetchAddedForecasters = ({ add, remove }) => {
    commitSelection(remove);
    dispatch(fetchMultipleUserForecasts({
      userIds: add, startDate, endDate, resourceId, horizonFilter, append: true,
    }));
  };

  // =========== 4) The "Apply Filters" button =============
  // `confirmed` skips the performance warning (its Proceed button).
  const applyFilters = (confirmed) => {
    const plan = planFetch();
    if (plan.kind === 'local') {
      commitSelection(plan.remove);
      return;
    }

    const userCount = selectedForecast.filter(f => f.id !== null).length;
    const days = moment(endDate).diff(moment(startDate), 'days') + 1;

    // 1. Check 10-day limit if users selected
    if (userCount > 0 && days > 10) {
      toast.error('Date range limited to 10 days when viewing individual user forecasts. Please adjust your date range or deselect forecasters.');
      return;
    }

    // 2. Warn before a fetch that loads 10+ forecasters: the whole selection
    // for a full refetch, only the added ones for an addition.
    if (!confirmed && plan.add.length >= 10) {
      const estimatedRecords = plan.add.length * days * 96;
      setShowPerformanceWarning(true);
      setQueryEstimate({ userCount: plan.add.length, days, estimatedRecords, adding: plan.kind === 'add' });
      return;
    }

    if (plan.kind === 'add') {
      fetchAddedForecasters(plan);
      return;
    }

    // Proceed with fetch
    setAppliedModel(model);
    setAppliedComparisonModels(comparisonModels);
    fetchDataIfNeeded(fetchSignature).then(() => {
    });
  };
  const handleApplyFilters = () => applyFilters(false);

  // Handler to proceed after 10+ users warning
  const handleConfirmLargeQuery = () => {
    setShowPerformanceWarning(false);
    applyFilters(true);
  };

  // Cancelling the warning on an addition restores the previous applied
  // selection, so nothing refetches: the additions are dropped and, for a
  // mixed change, the forecasters unticked with them come back (the fallback
  // after a remount with no snapshot only drops the additions). Cancelling a
  // full refetch means "just show the ensemble": drop the per-forecaster
  // selection back to ensemble-only so the heavy multi-forecaster query is
  // never issued. With no forecasters selected the debounced auto-apply effect
  // then fetches only the ensemble + observed for the current window (and the
  // picker/plot stay in sync, both showing the ensemble).
  const handleCancelLargeQuery = () => {
    setShowPerformanceWarning(false);
    const plan = planFetch();
    if (plan.kind === 'add') {
      dispatch(setSelectedForecast(
        appliedFiltersRef.current?.selectedForecast
          ?? selectedForecast.filter((f) => !plan.add.includes(f.id)),
      ));
      return;
    }
    dispatch(setSelectedForecast(DEFAULT_SELECTED_FORECAST));
  };

  // Opens the cross-market performance pane. Current month always; track/horizon
  // seeded from the monthly filters; forecasters are locked to their own id.
  const handleOpenPerformancePane = (targetUserId = null) => {
    const now = moment();
    dispatch(openPane({
      userId: isForecaster ? currentUser?.id : targetUserId,
      track: monthlyTrack,
      horizonOffset: monthlyHorizon,
      year: now.year(),
      month: now.month() + 1,
    }));
  };

  // =========== 4b) Auto-apply filter changes (debounced) ===========
  // Every filter change updates Redux immediately, shifting fetchSignature.
  // Re-run Apply after a short debounce so rapid edits (e.g. toggling
  // forecasters one-by-one in the side panel, or picking a date range)
  // collapse into a single fetch. Routing through handleApplyFilters keeps
  // the guardrails (10-day cap, 10+ forecaster warning) on the settled
  // selection. The signature guard prevents loops: once the fetch (or a
  // forecaster change applied without one) records the new signature,
  // fetchSignature === lastFetchSignature and we stop.
  useEffect(() => {
    if (!startDate || !endDate) return undefined;
    if (fetchSignature === lastFetchSignature) return undefined;
    const timer = setTimeout(() => {
      handleApplyFilters();
    }, 500);
    return () => clearTimeout(timer);
  }, [fetchSignature, lastFetchSignature, startDate, endDate]); // eslint-disable-line react-hooks/exhaustive-deps

  // =========== 6) Derived Stats (unchanged) ===========
  // "Active users" comes from the backend submission-stats aggregate, not from
  // usersList: the market-owner directory payload strips `last_login` (audit
  // A260430-API-M6), so a client-side filter would always yield 0 for MOs.

  // Shares the single "recent activity" window (activeUsersDays) so the whole
  // top row is consistently "last N days".
  const newRegistrationsCount = useMemo(() => {
    return usersList.filter((user) => {
      return moment().diff(moment(user.date_joined), 'days') <= activeUsersDays;
    }).length;
  }, [usersList, activeUsersDays]);

  const recentStats = [
    {
      title: 'Active users',
      count: activeUsers,
      note: `logged in within the last ${activeUsersDays} days`,
    },
    {
      title: 'Active forecasters',
      count: activeForecasters,
      note: `submitted in the last ${activeUsersDays} days`,
    },
    {
      title: 'New registrations',
      count: newRegistrationsCount,
      note: `registered in the last ${activeUsersDays} days`,
    },
  ];

  const totalStats = [
    {
      title: 'Total users',
      count: usersList.length,
      note: 'users registered in the platform',
    },
    {
      title: 'Forecasts',
      count: totalSubmissions,
      note: 'forecast submissions to date',
    },
    {
      title: 'Sessions run',
      count: marketSessions.length,
      note: 'market sessions executed to date',
    },
  ];

  // =========== 7) Render ===========
  // Chart filters/actions: desktop shows them inline in the chart card; on
  // phones privileged roles open them from the full-screen chart view, while
  // forecasters use the chart chips and ChartOptionsSheet.
  const chartActionsProps = {
    startDate,
    endDate,
    model,
    includeEnsemble,
    anonymize,
    reportDownloadLoading,
    selectedResource,
    handleApplyFilters,
    hasPendingChanges: fetchSignature !== lastFetchSignature,
    horizonFilter,
    availableHorizons,
    previewMode: effectivePreviewMode,
    onPreviewModeChange: setPreviewMode,
    hasPendingPreview,
    hasChartData:
      (rawData && rawData.length > 0) ||
      (ensembleForecasts && ensembleForecasts.length > 0) ||
      (individualUserForecasts && Object.keys(individualUserForecasts).length > 0),
  };

  // Publisher of the observed series, credited on the chart's legend entry.
  const observedSource = resourceSource(selectedResource);

  // The chart gets only the selected forecasters, so an untick hides its line
  // at once; the store drops it when the change is applied. Same object when
  // nothing is filtered out.
  const chartUserForecasts = useMemo(() => {
    const selected = new Set((selectedForecast || []).map((f) => f.id));
    const entries = Object.entries(individualUserForecasts || {});
    const shown = entries.filter(([id]) => selected.has(id));
    return shown.length === entries.length ? individualUserForecasts : Object.fromEntries(shown);
  }, [individualUserForecasts, selectedForecast]);

  // Unticking Ensemble hides its line and band; the data stays loaded, so
  // ticking it again is instant.
  const showEnsemble = selectedIds.includes(ENSEMBLE_ID);

  // Shared by the inline chart and the phone full-screen overlay.
  const forecastChartProps = {
    ensembleForecast: isAdmin || isMarketOwner || isMarketOwnerReadOnly
      ? (showEnsemble ? ensembleForecasts : NO_SERIES)
      : forecasterChartForecast,
    individualUserForecasts: chartUserForecasts,
    rawData: rawData || [],
    isForecaster,
    loading: isTransitioning || (loadingData && appendsInFlight === 0),
    comparisonForecasts,
    continuousPreview: previewForChart,
    horizonFilter,
    showUserLegend: true,
    anonymizeUsers: !isAdmin && (isMarketOwner || isMarketOwnerReadOnly),
    blurSensitive,
    userAliases,
    units: selectedResource?.units,
    sourceName: observedSource.name,
    sourceUrl: observedSource.url,
    // Forecasters read the horizon from the phone header and chips, so the
    // chart's own phone caption is dropped for them (card and overlay).
    showPhoneCaption: isPrivilegedUser,
    // The forecaster picker adds and removes series in place, so for these
    // roles the fade follows the applied base: it changes only when
    // fetchDataIfNeeded starts a full refetch (the chart is then behind the
    // spinner, and the new data fades in), never on a forecaster change and
    // never on an edit that has not been fetched yet.
    animationKey: isPrivilegedUser ? (signatureBase(lastFetchSignature) ?? '') : undefined,
    insights: insightsOn ? insightsData : null,
    insightsOn,
    insightsOptions,
    // The pin row is kept above the plot whenever the layer can be used here,
    // so turning it on or off never moves the plot's top.
    insightsReserve: insightsAvailable && isDesktop,
  };

  // Phone chart chips and options sheet, forecasters only: privileged roles
  // keep ChartDashboardActions' phone filters (the admin phase will unify them).
  const chartInactive = selectedResource?.is_active === false;
  const chartUnit = resourceUnitMeta(selectedResource?.units).unit;
  const phoneChips = [
    horizonFilter === 'latest' ? 'Latest' : (Number(horizonFilter) === 0 ? 'Intraday' : `D+${horizonFilter}`),
    periodChipLabel(startDate, endDate),
    ...(hasPendingPreview ? [PREVIEW_SHORT[effectivePreviewMode]] : []),
  ];
  // A changed horizon or window shifts fetchSignature and the debounced
  // auto-apply fetches. With nothing changed, Apply re-runs the guarded fetch
  // like the old "Apply Filters" button, so it still retries an empty chart.
  const applyChartOptions = ({ horizonFilter: h, startDate: s, endDate: e, previewMode: p }) => {
    dispatch(setHorizonFilter(h));
    dispatch(setDateRange({ startDate: s, endDate: e }));
    setPreviewMode(p);
    if (h === horizonFilter && s === startDate && e === endDate) handleApplyFilters();
  };

  // Monthly cards for the forecaster view (Task 25): built once so MonthlyTabs
  // can show them as phone tabs while their lg:order-* classes keep placing
  // them in the desktop grid the same way Task 24 left it (Daily rank 1,
  // League 2, Leaderboard 3, Submissions 4).
  const leaderboardCard = (
    <Card className="lg:order-3 flex flex-col">
      <MonthlyLeaderboard
        resourceId={resourceId}
        activeUserId={currentUser?.id || null}
        isAdmin={false}
        track={monthlyTrack}
        year={monthlyYear}
        month={monthlyMonth}
        horizon={monthlyHorizon}
        onForecasterClick={showSide ? (uid) => handleOpenPerformancePane(uid) : undefined}
      />
    </Card>
  );
  const dailyRankCard = (
    <Card className="lg:order-1 flex flex-col">
      <CardHeader title="Daily rank" description="Your average daily rank this month, with the latest day highlighted." className="mb-3" />
      <div className="flex-1 min-h-0 flex"><MonthlyDailyRanks activeUserId={currentUser?.id || null} /></div>
    </Card>
  );
  const leagueCard = (
    <Card className="lg:order-2">
      <MonthlyPerformanceCard
        year={monthlyYear}
        month={monthlyMonth}
        horizon={monthlyHorizon}
      />
    </Card>
  );
  const submissionsCard = (
    <Card className="lg:order-4 flex flex-col">
      <MonthlySubmissions
        year={monthlyYear}
        month={monthlyMonth}
        horizon={monthlyHorizon}
      />
    </Card>
  );

  return (
    <div>
      {/* Performance Warning Dialog */}
      <ConfirmDialog
        open={showPerformanceWarning}
        title="Performance Warning: Many Users Selected"
        description={`You've ${queryEstimate.adding ? 'added' : 'selected'} ${queryEstimate.userCount} users. This will fetch approximately ${queryEstimate.estimatedRecords?.toLocaleString()} forecast records and may take 15-30 seconds to load.`}
        warnings={[
          { label: "Performance", description: "Large queries may cause temporary slowdowns" },
          { label: "Recommendation", description: "Consider selecting fewer users (< 10) for better performance" }
        ]}
        confirmText="Proceed Anyway"
        cancelText="Cancel"
        onConfirm={handleConfirmLargeQuery}
        onCancel={handleCancelLargeQuery}
      />

      <div className="grid grid-cols-12 gap-4 items-start">
        {/* Row 0: platform notices (maintenance, clock changes); renders
            nothing when there are none. */}
        <DashboardNotices className="col-span-12 min-w-0" />

        {/* Row 1: active session status across full width */}
        <div className="col-span-12 min-w-0">
          <StatusHero />
        </div>

        <AlsoDueRow className="col-span-12" />

        {/* Phone-only standing strip: latest day, month rank, league, and
            the phone's only entry into the cross-market pane (the desktop
            right-edge handle strip stays, below). */}
        {isForecaster && (
          <MobileRankStrip
            className="col-span-12"
            userId={currentUser?.id || null}
            onOpenPane={() => handleOpenPerformancePane()}
          />
        )}

        {/* Row 2: chart column. min-w-0 lets the track shrink below the
            uplot canvas's intrinsic pixel width; without it the first wide
            paint locks the whole grid wider than the phone viewport. */}
        <div className="col-span-12 min-w-0">
          <Card padding="compact" className={insightsAvailable ? 'relative' : ''}>
            {/* Desktop chip bar. On phones forecasters get the header and chips
                below; privileged roles keep their filters in the expanded
                chart view (the admin phase will unify them). */}
            <div className="hidden lg:block">
              <ChartDashboardActions {...chartActionsProps} />
            </div>

            {!isPrivilegedUser && (
              <div className="lg:hidden">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <h2 className="text-[15px] leading-5 font-semibold tracking-tight text-gray-900 dark:text-gray-100">Forecast vs measured</h2>
                    <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">
                      <span className="font-semibold italic text-brand-600 dark:text-brand-400">{phoneChips[0]}</span> forecast · {chartUnit} · UTC
                    </p>
                  </div>
                  <button type="button" onClick={() => setChartExpanded(true)} aria-label="Expand chart" className="-mr-2 -mt-2 flex h-11 w-11 flex-none items-center justify-center rounded-lg text-gray-600 hover:bg-gray-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 dark:text-gray-300 dark:hover:bg-gray-800">
                    <ArrowsPointingOutIcon className="h-5 w-5" aria-hidden="true" />
                  </button>
                </div>
                <ChartChips chips={phoneChips} inactive={chartInactive} onOpen={() => setChartSheetOpen(true)} />
              </div>
            )}

            <div className="relative h-[340px] lg:h-[425px] mt-3 lg:mt-2">
              <ForecastComparisonChartUplot {...forecastChartProps} />
            </div>

            {/* Phone-only escape hatch to a full-viewport chart (privileged
                roles; forecasters have the expand button in the header) */}
            {isPrivilegedUser && (
              <button
                type="button"
                onClick={() => setChartExpanded(true)}
                className="lg:hidden mt-2 w-full flex items-center justify-center gap-1.5 rounded-lg border border-gray-200/70 dark:border-gray-600/70 py-2 text-xs font-semibold text-brand-600 dark:text-brand-400"
              >
                <ArrowsPointingOutIcon className="h-4 w-4" aria-hidden="true" />
                Expand chart
              </button>
            )}

            {/* Opened from the Insights menu, which can show it before insights are on */}
            {insightsAvailable && (
              <AboutInsightsPanel
                open={insightsAboutOpen}
                onClose={() => dispatch(setAboutOpen(false))}
                params={insightsData?.params}
                rampMethod={insightsRampMethod}
                noForecastRamps={rowsWithoutForecastRamps(insightsData)}
                tab={insightsAboutTab}
                tabKey={insightsAboutTabSeq}
              />
            )}
          </Card>

          {(isAdmin || isMarketOwner) && (
            <div className="mt-4 mb-4">
              <PerHorizonComparison
                resourceId={resourceId}
                startDate={startDate}
                endDate={endDate}
                participants={participants}
                appliedModel={appliedModel}
                appliedComparisonModels={appliedComparisonModels}
              />
            </div>
          )}

          {/* Desktop section header for the monthly cards below (phone row is next) */}
          <div className="hidden lg:block mt-8">
            <MonthlySectionHeader
              track={monthlyTrack}
              onTrack={setMonthlyTrack}
              horizon={monthlyHorizon}
              onHorizon={setMonthlyHorizon}
              horizons={monthlyHorizons}
              year={monthlyYear}
              month={monthlyMonth}
              onMonth={(y, m) => { setMonthlyYear(y); setMonthlyMonth(m); }}
            />
          </div>

          {/* Phone "This month" row: scope chip opens the horizon + month
              sheet, full-width track control sits below it. */}
          <div className="lg:hidden mt-6 space-y-3">
            <div className="flex items-center justify-between gap-3">
              <div className="min-w-0">
                <h2 className="text-[17px] leading-6 font-semibold tracking-tight text-gray-900 dark:text-gray-100">This month</h2>
                <p className="text-xs text-gray-500 dark:text-gray-400">{moment({ year: monthlyYear, month: monthlyMonth - 1 }).format('MMMM YYYY')}</p>
              </div>
              <button type="button" onClick={() => setScopeOpen(true)} aria-haspopup="dialog" className="inline-flex h-10 flex-none items-center gap-1.5 rounded-full border border-gray-200/70 bg-surface px-3.5 text-[13px] font-medium text-gray-800 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 dark:border-gray-600/70 dark:bg-gray-870 dark:text-gray-100">
                {monthlyHorizon == null ? '-' : (monthlyHorizon === 0 ? 'Intraday' : `D+${monthlyHorizon}`)} · {moment({ year: monthlyYear, month: monthlyMonth - 1 }).format('MMM YYYY')}
                <ChevronDownIcon className="h-3 w-3 text-gray-400" aria-hidden="true" />
              </button>
            </div>
            <TrackSegmentedControl selectedTrack={monthlyTrack} onSelectTrack={setMonthlyTrack} showMetric fullWidth size="lg" />
          </div>

          {/* Leaderboard + Overview */}
          {isForecaster && !showSide ? (
            // Forecaster view: 2x2 grid at lg so cards share baselines; below
            // lg, MonthlyTabs shows the same cards as three labelled tabs
            // (Leaderboard / Ranks / Submissions) instead of the grid, one
            // panel at a time so it takes only that panel's height. The
            // lg:order-* classes on each card restore the original desktop
            // placement: ranks + league qualification in the auto top row,
            // leaderboard + submissions in the 1fr bottom row. Keep the tabs
            // list and the lg:order-* classes in sync when reordering.
            <MonthlyTabs
              // lg:mt-6 here relies on Tailwind emitting it after MonthlyTabs' own lg:mt-0.
              className="lg:grid-cols-2 lg:grid-rows-[auto_1fr] lg:gap-4 lg:mt-6"
              tabs={[
                { id: 'leaderboard', label: 'Leaderboard', content: leaderboardCard },
                { id: 'ranks', label: 'Ranks', content: (<>{dailyRankCard}{leagueCard}</>) },
                { id: 'submissions', label: 'Submissions', content: submissionsCard },
              ]}
            />
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4 lg:mt-6">
              <div className="flex flex-col h-full">
                <div className="sm:bg-surface sm:dark:bg-gray-870 sm:rounded-xl sm:p-5 flex-1 flex flex-col sm:border sm:border-gray-200/70 sm:dark:border-gray-600/70 sm:shadow-sm">
                  <MonthlyLeaderboard
                    resourceId={resourceId}
                    activeUserId={isForecaster ? (currentUser?.id || null) : null}
                    isAdmin={isAdmin || isMarketOwner || isMarketOwnerReadOnly}
                    track={monthlyTrack}
                    year={monthlyYear}
                    month={monthlyMonth}
                    horizon={monthlyHorizon}
                    onForecasterClick={showSide ? (uid) => handleOpenPerformancePane(uid) : undefined}
                  />
                </div>
              </div>
              {showSide && (
                <div className="flex flex-col h-full">
                  <div className="sm:bg-surface sm:dark:bg-gray-870 sm:rounded-xl sm:p-5 h-full flex flex-col sm:border sm:border-gray-200/70 sm:dark:border-gray-600/70 sm:shadow-sm">
                    <div className="mb-3">
                      <h2 className="text-lg font-bold text-gray-900 dark:text-gray-100">Platform overview</h2>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">Key platform metrics for the current workspace</p>
                    </div>
                    <div className="space-y-5">
                      <div className="flex items-center gap-3">
                        <div className="flex-1 min-w-0">
                          <StatsCard stats={recentStats} variant="kpi" />
                        </div>
                        <div className="w-16 shrink-0 text-right text-[10px] font-medium uppercase tracking-wider leading-tight text-gray-400 dark:text-gray-500">
                          Last {activeUsersDays} days
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <div className="flex-1 min-w-0">
                          <StatsCard stats={totalStats} variant="kpi" />
                        </div>
                        <div className="w-16 shrink-0 text-right text-[10px] font-medium uppercase tracking-wider leading-tight text-gray-400 dark:text-gray-500">
                          Overall
                        </div>
                      </div>
                    </div>
                    <div className="mt-6 pt-5 border-t border-gray-200/70 dark:border-gray-600/70 flex-1 flex flex-col min-h-0">
                      <div className="text-[11px] font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-3">
                        Submissions · last 90 days
                      </div>
                      <div className="flex-1 min-h-[120px]">
                        <SubmissionsActivityChart data={submissionActivity} />
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
         </div>
      </div>

      <PerformancePaneHandle onOpen={() => handleOpenPerformancePane()} />
      <PerformancePane availableHorizons={monthlyHorizons} />

      <FullScreenChartOverlay
        open={chartExpanded}
        onClose={() => setChartExpanded(false)}
        title="Forecast vs measured"
      >
        {/* Portrait: filters above the plot. Landscape: privileged roles keep
            their filter stack in a narrow left rail so the plot gets the full
            (scarce) height; the forecaster chip row is one line, so it stays
            on top and the plot keeps the full width. */}
        <div className={`flex h-full flex-col ${isPrivilegedUser ? 'landscape:flex-row landscape:gap-3' : ''}`}>
          {isPrivilegedUser ? (
            <div className="landscape:w-52 landscape:shrink-0 landscape:overflow-y-auto landscape:pr-1">
              <ChartDashboardActions {...chartActionsProps} />
            </div>
          ) : (
            <ChartChips chips={phoneChips} inactive={chartInactive} onOpen={() => setChartSheetOpen(true)} />
          )}
          <div className={`relative flex-1 min-h-0 mt-2 ${isPrivilegedUser ? 'landscape:mt-0' : ''}`}>
            <ForecastComparisonChartUplot {...forecastChartProps} />
          </div>
        </div>
      </FullScreenChartOverlay>

      {/* One sheet for the card chips and the overlay chips. It gets the raw
          previewMode, not the effective one, so applying while Show is hidden
          keeps the forecaster's stored choice. */}
      {!isPrivilegedUser && (
        <ChartOptionsSheet
          open={chartSheetOpen}
          onClose={() => setChartSheetOpen(false)}
          horizonFilter={horizonFilter}
          availableHorizons={availableHorizons}
          startDate={startDate}
          endDate={endDate}
          previewMode={previewMode}
          hasPendingPreview={hasPendingPreview}
          onApply={applyChartOptions}
        />
      )}

      <MonthScopeSheet
        open={scopeOpen}
        onClose={() => setScopeOpen(false)}
        horizon={monthlyHorizon}
        horizons={monthlyHorizons}
        year={monthlyYear}
        month={monthlyMonth}
        onApply={({ horizon, year, month }) => { setMonthlyHorizon(horizon); setMonthlyYear(year); setMonthlyMonth(month); }}
      />
    </div>
  );
}
